import { useCallback } from "react";

import { useBxpWebStargateClient } from "@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client";

import {
    getL1Assessments,
    getMigrationPlanTotals,
    getInstances
} from "@/state/actions";
import { useFastshiftDispatch, useFastshiftSelector } from "@/state/hook";

const CONSOLIDATED_INSIGHTS_STATUS_HAS_ERRORS = "HAS_ERRORS";

export const useSummary = () => {
    const dispatch = useFastshiftDispatch();
    const stargateClient = useBxpWebStargateClient();

    const loadL1Assessments = useCallback(
        (orgId: string) => {
            dispatch(getL1Assessments({ stargateClient, orgId }));
        },
        [dispatch, stargateClient]
    );

    const loadMigrationPlanTotals = useCallback(
        (orgId: string) => {
            dispatch(getMigrationPlanTotals({ stargateClient, orgId }));
        },
        [dispatch, stargateClient]
    );

    const loadInstances = useCallback(
        (orgId: string) => {
            dispatch(getInstances({ stargateClient, orgId }));
        },
        [dispatch, stargateClient]
    );

    const l1Assessments = useFastshiftSelector((state) => state.l1Assessments);
    const migrationPlanTotals = useFastshiftSelector(
        (state) => state.migrationPlanTotals
    );
    const instances = useFastshiftSelector((state) => state.instances);
    const selectedDomainOrg = useFastshiftSelector(
        (state) => state.selectedDomainOrg
    );

    const isL1AssessmentsLoading = l1Assessments.pending || !selectedDomainOrg;

    const isMigrationPlanTotalsLoading =
        migrationPlanTotals.pending || !selectedDomainOrg;

    const isInstancesLoading = instances.pending || !selectedDomainOrg;

    const summaryData = {
        dataCenter: {
            count: l1Assessments.data.length
        },
        areaImprovement: {
            count: l1Assessments.data.filter(
                ({ consolidatedInsightsStatus }) =>
                    consolidatedInsightsStatus ===
                    CONSOLIDATED_INSIGHTS_STATUS_HAS_ERRORS
            ).length
        },
        deletedApps: {
            count: instances.data.totalCount
        },
        migrationCompleted: {
            count: migrationPlanTotals.data.totalPlansCompleted
        },
        migrationInProgress: {
            count: migrationPlanTotals.data.totalPlansRunning
        },
        unsuccessfulMigration: {
            count: migrationPlanTotals.data.totalPlansUnsuccessful
        }
    };

    // isEmpty means there is no data and no errors and not loading
    const isEmpty =
        l1Assessments.data.length === 0 &&
        instances.data.totalCount === 0 &&
        migrationPlanTotals.data.totalPlansCompleted === 0 &&
        migrationPlanTotals.data.totalPlansUnsuccessful === 0 &&
        migrationPlanTotals.data.totalPlansRunning === 0 &&
        !isL1AssessmentsLoading &&
        !isMigrationPlanTotalsLoading &&
        !isInstancesLoading &&
        !l1Assessments.error &&
        !instances.error &&
        !migrationPlanTotals.error;

    const errors = {
        l1Assessments: l1Assessments.error,
        migrationPlanTotals: migrationPlanTotals.error,
        instances: instances.error
    };

    return {
        summaryData,
        isL1AssessmentsLoading,
        isMigrationPlanTotalsLoading,
        isInstancesLoading,
        isEmpty,
        errors,
        loadL1Assessments,
        loadMigrationPlanTotals,
        loadInstances
    };
};
